/**
 * MetricInfoButton.tsx — Accessible info icon for per-metric help text.
 *
 * Uses Radix Popover (via shadcn) — NOT Tooltip — so content is hoverable
 * and keyboard-dismissible per WCAG 1.4.13 (non-hover content dismissal).
 *
 * Props:
 *   metricKey — ScoreKey string used to look up shortDesc in SCORE_DEFS.
 *   label     — Human-readable metric name, used in aria-label + fallback content.
 *
 * Touch target: p-3 -m-3 technique gives 44×44 px minimum target area.
 */
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Info } from 'lucide-react'
import { SCORE_DEFS } from '@/lib/scoreDefinitions'
import type { ScoreKey } from '@/lib/scoreDefinitions'

export interface MetricInfoButtonProps {
  metricKey: string
  label: string
}

export function MetricInfoButton({ metricKey, label }: MetricInfoButtonProps) {
  const desc = SCORE_DEFS[metricKey as ScoreKey]?.shortDesc ?? label

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label={`About ${label}`}
          className="inline-flex items-center justify-center p-3 -m-3
                     text-muted-foreground hover:text-foreground
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                     rounded-sm min-h-[44px] min-w-[44px]"
        >
          <Info size={16} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="glass w-64 rounded-xl border border-border/60 p-3 text-xs shadow-lg"
        side="top"
      >
        <p className="leading-relaxed text-foreground">{desc}</p>
      </PopoverContent>
    </Popover>
  )
}
